import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home,
  PauseCircle,
  PlayCircle,
  ChevronLeftCircle,
  ChevronRightCircle,
} from "lucide-react";
import "../../styles/global.css";
import { APP_CONSTANTS } from "../../utilities/app_constants";
import AsyncStorage from "@react-native-async-storage/async-storage";
import network_constants from "../../utilities/network_constants";
import { setDate } from "../../store/reports/actions";
import { useDispatch, useSelector } from "react-redux";

const ExternalImagesReport = ({
  reportData,
  onNext,
  onPrevious,
  onPause,
  isPaused,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [imageUrl, setImageUrl] = useState(null);
  const [name, setName] = useState(null);
  const [note, setNote] = useState("");

  const { currentDate } = useSelector((state) => state.report);

  useEffect(() => {
    const fetchImageWithAuth = async () => {
      if (!reportData?.url) return;

      const urlParams = new URLSearchParams(reportData.url.split("?")[1]);
      const imageId = urlParams.get("id");
      const name = urlParams.get("Name");
      const note = urlParams.get("Note");
      
      if (name) {
        setName(name);
      }
      
      if (note) {
        setNote(note);
      }

      if (!imageId) {
        console.error("❌ No image ID found in URL");
        return;
      }

      const imageUrl = `${network_constants.getImage}?id=${imageId}`;

      const username = await AsyncStorage.getItem("username");
      const password = await AsyncStorage.getItem("password");

      try {
        const response = await fetch(imageUrl, {
          headers: {
            Authorization: "Basic " + btoa(`${username}:${password}`),
          },
        });

        if (!response.ok) throw new Error("Bad response");

        const contentType = response.headers.get("Content-Type");
        if (!contentType?.includes("image")) throw new Error("Not an image");

        const blob = await response.blob();
        const objectURL = URL.createObjectURL(blob);
        setImageUrl(objectURL);
      } catch (error) {
        console.error("🚨 Failed to fetch image:", error.message);
        setImageUrl(null);
      }
    };

    fetchImageWithAuth();
  }, [reportData?.url]);

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };
  
  useEffect(() => {
    const date = getCurrentDate();
    dispatch(setDate(date));
  }, [dispatch]);

  // Auto-Navigate after X seconds
  useEffect(() => {
    if (!isPaused) {
      const intervalInSeconds = parseInt(reportData?.interval, 10);
      const displayTime =
        !isNaN(intervalInSeconds) && intervalInSeconds > 0
          ? intervalInSeconds * 1000
          : APP_CONSTANTS.REPORT_DISPLAY_TIME;

      console.log("⏳ Auto-navigate in:", displayTime, "ms");

      const timeout = setTimeout(() => {
        console.log("➡️ Auto-navigating to next report");
        onNext();
      }, displayTime);

      return () => {
        console.log("🧹 Clearing timeout");
        clearTimeout(timeout);
      };
    }
  }, [onNext, isPaused, reportData?.interval]);

  // Add responsive styles

  const scrollingText = `${note || ""}`;
  // Replace your responsive useEffect with this updated version
useEffect(() => {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = `
    /* Responsive Styles - UI保持不变 */
    .container {
      max-width: 100%;
      width: 100%;
      overflow-x: hidden;
    }
    
    /* Large screens 1920px and above - NO CHANGES, keep as is */
    /* No styles for 1920px+ means original UI remains unchanged */
    
    /* Header responsive - ONLY for screens below 1366px */
    @media (max-width: 1365px) {
      .header {
        flex-wrap: wrap !important;
        justify-content: center !important;
        gap: 8px !important;
        padding: 10px !important;
      }
      
      .header h1 {
        order: 2;
        width: 100%;
        text-align: center;
        margin: 5px 0 !important;
      }
      
      .controls {
        order: 1;
      }
      
      .home-button {
        order: 0;
      }
      
      .date-display {
        order: 3;
        margin-left: auto !important;
      }
      
      .logo-container {
        order: 4;
      }
    }
    
    /* Tablet devices (768px to 1365px) */
    @media (min-width: 768px) and (max-width: 1365px) {
      .header {
        gap: 10px !important;
      }
    }
    
    /* Mobile devices (below 768px) */
    @media (max-width: 767px) {
      .container {
        padding: 0 8px;
      }
      
      .controls svg {
        width: 28px !important;
        height: 28px !important;
      }
      
      .home-button svg {
        width: 24px !important;
        height: 28px !important;
      }
      
      .current-date {
        width: 65px !important;
        min-width: 65px !important;
        font-size: 10px !important;
        padding: 2px 4px !important;
      }
      
      .date-display span {
        font-size: 10px !important;
      }
      
      .logo-container img {
        height: 32px !important;
      }
      
      .image-gallery {
        padding: 10px !important;
        min-height: calc(100vh - 100px) !important;
      }
      
      .header h1 {
        font-size: 16px !important;
      }
    }
    
    /* Small mobile devices (below 640px) */
    @media (max-width: 639px) {
      .date-display {
        gap: 4px !important;
      }
      
      .date-display span {
        font-size: 9px !important;
      }
      
      .current-date {
        width: 55px !important;
        font-size: 9px !important;
        padding: 2px 3px !important;
      }
      
      .image-gallery {
        padding: 5px !important;
      }
    }
    
    /* Image responsive - ONLY for screens below 1366px */
    @media (max-width: 1365px) {
      .report-image {
        max-width: 100%;
        width: auto;
        height: auto;
        object-fit: contain;
      }
    }
    
    @media (max-width: 767px) {
      .report-image {
        max-height: 65vh !important;
        border-radius: 8px !important;
      }
    }
    
    @media (max-width: 639px) {
      .report-image {
        max-height: 55vh !important;
        border-radius: 6px !important;
      }
    }
    
    /* Image gallery container - responsive only below 1366px */
    @media (max-width: 1365px) {
      .image-gallery {
        padding: 20px;
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: calc(100vh - 120px);
      }
      
      .image-card {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
    
    /* For screens below 768px */
    @media (max-width: 767px) {
      .image-gallery {
        min-height: calc(100vh - 100px) !important;
      }
    }
    
    /* Keep marquee animation */
    @keyframes marquee {
      0% {
        transform: translateX(0);
      }
      100% {
        transform: translateX(-50%);
      }
    }
    
    /* Scrolling text responsive - ONLY for mobile */
    @media (max-width: 767px) {
      div[style*="position: fixed"][style*="bottom: 40px"] {
        bottom: 20px !important;
      }
      
      div[style*="animation: marquee"] {
        font-size: 14px !important;
      }
      
      div[style*="animation: marquee"] span {
        padding-right: 80px !important;
      }
    }
    
    @media (max-width: 639px) {
      div[style*="animation: marquee"] {
        font-size: 12px !important;
        padding: 8px 0 !important;
      }
      
      div[style*="animation: marquee"] span {
        padding-right: 60px !important;
      }
    }
  `;
  document.head.appendChild(styleSheet);
  return () => {
    document.head.removeChild(styleSheet);
  };
}, []);
  return (
    <div className="container" style={{ position: "relative", minHeight: "100vh" }}>
      <header
        className="header responsive-header"
        style={{
          flexWrap: "wrap",
          gap: "1px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <button
          className="home-button responsive-home-btn"
          onClick={() =>
            navigate("/menu", {
              state: { hideModal: true },
            })
          }
        >
          <Home
            className="home-icon"
            size={120}
            style={{
              width: "35px",
              height: "40px",
            }}
          />
        </button>
        <div
          className="controls responsive-controls"
          style={{ display: "flex", gap: "1px", alignItems: "center" }}
        >
          <ChevronLeftCircle
            size={40}
            onClick={onPrevious}
            style={{ cursor: "pointer" }}
          />
          {isPaused ? (
            <PlayCircle
              size={40}
              onClick={() => onPause(false)}
              style={{ cursor: "pointer" }}
            />
          ) : (
            <PauseCircle
              size={40}
              onClick={() => onPause(true)}
              style={{ cursor: "pointer" }}
            />
          )}
          <ChevronRightCircle
            size={40}
            onClick={onNext}
            style={{ cursor: "pointer" }}
          />
        </div>
        <h1 style={{ 
          fontSize: "clamp(16px, 4vw, 28px)", 
          margin: "5px 0",
          fontWeight: "bold",
        }}>
          {name || "External Images Report"}
        </h1>
        <div
          className="date-display responsive-date"
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <span>Current Date:</span>
          <input
            type="text"
            value={currentDate}
            readOnly
            className="current-date"
            style={{
              width: "80px",
              minWidth: "80px",
              padding: "4px 8px",
              fontSize: "14px",
            }}
          />
        </div>
        <div className="logo-container responsive-logo">
          <a
            href="https://www.digiopenservices.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/Logo.png"
              alt="Logo"
              className="header-logo"
              style={{
                maxwidth: "110px",
                height: "40px",
                objectFit: "contain",
                borderRadius: "6px",
                margin: "2px 10px",
                cursor: "pointer",
              }}
            />
          </a>
        </div>
      </header>

      <div className="image-gallery">
        {!imageUrl ? (
          <p
            style={{ 
              textAlign: "center", 
              fontSize: "clamp(16px, 4vw, 20px)", 
              marginTop: "20px",
              padding: "0 20px",
            }}
          >
            No valid image found or failed to load.
          </p>
        ) : (
          <div className="image-card" style={{ textAlign: "center" }}>
            <img
              src={imageUrl}
              alt="External Report"
              className="report-image"
              style={{
                maxWidth: "100%",
                maxHeight: "calc(100vh - 120px)",
                width: "auto",
                height: "auto",
                display: "block",
                margin: "0 auto",
                borderRadius: "12px",
                boxShadow: "0 0 20px rgba(0,0,0,0.3)",
                objectFit: "contain",
              }}
              onLoad={() => console.log("🖼️ Image loaded successfully")}
              onError={(e) => {
                console.error("❌ Failed to load image");
                e.target.src = "";
              }}
            />
          </div>
        )}
      </div>

     <div
  style={{
    position: "fixed",
    bottom: "40px",
    left: 0,
    right: 0,
    color: "#022fe3",
    padding: "12px 0",
    overflow: "hidden",
    whiteSpace: "nowrap",
    fontFamily: "monospace",
    fontSize: "25px",
    fontWeight: "700",
    zIndex: 1000,
  }}
>
  <div
    style={{
      display: "inline-block",
      animation: "scrollRightToLeft 35s linear infinite",
      whiteSpace: "nowrap",
      paddingLeft: "100%", 
    }}
  >
    <span>{scrollingText}</span>
  </div>

  <style>
    {`
      @keyframes scrollRightToLeft {
        0% {
          transform: translateX(0%);
        }
        100% {
          transform: translateX(-100%);
        }
      }
    `}
  </style>
</div> 
    </div>
  );
};

export default ExternalImagesReport;